import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Lock, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

/**
 * Full-page PDF viewer for sharing — view-only mode.
 * - PDF.js toolbar disabled (#toolbar=0) → no native Download / Print buttons
 * - Right-click disabled on the viewer area
 * - Iframe is sandboxed (no top-frame navigation toward the file URL)
 * - The direct file URL is not visible in the address bar (URL stays /documents/view/:id)
 */
export default function DocumentViewerPage() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDoc = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/documents/${id}`);
      setDoc(res.data);
      // Track consultation
      axios.post(`${API}/documents/${id}/download`).catch(() => {});
    } catch (e) {
      setError(e?.response?.status === 404 ? 'Document introuvable.' : 'Erreur de chargement.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchDoc(); }, [fetchDoc]);

  useEffect(() => {
    if (doc?.title) document.title = `${doc.title} — CRESA ASBL`;
  }, [doc]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <Loader2 className="animate-spin text-[#4A6B48]" size={36} />
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F9F8F6] gap-4 px-6 text-center">
        <p className="text-lg text-[#2E332D] font-semibold">{error || 'Document indisponible'}</p>
        <Link to="/documents" className="inline-flex items-center gap-2 text-[#4A6B48] hover:underline">
          <ArrowLeft size={16} /> Retour aux documents
        </Link>
      </div>
    );
  }

  const fileUrl = resolveUrl(doc.file_url);
  const viewerSrc = `${fileUrl}#toolbar=0&navpanes=0&scrollbar=1&statusbar=0&messages=0&view=FitH`;

  return (
    <div className="min-h-screen flex flex-col bg-[#2E332D]" onContextMenu={(e) => e.preventDefault()} data-testid="document-viewer-page">
      <header className="bg-white border-b border-[#E5E3DB] px-4 py-3 flex items-center justify-between gap-3 shadow-sm">
        <div className="flex items-center gap-3 min-w-0">
          <Link
            to="/documents"
            data-testid="back-to-documents"
            className="inline-flex items-center gap-1 text-sm font-bold text-[#4A6B48] hover:underline shrink-0"
          >
            <ArrowLeft size={16} /> Retour
          </Link>
          <span className="hidden sm:inline text-[#E5E3DB]">|</span>
          <h1 className="font-bold text-[#2E332D] truncate" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {doc.title}
          </h1>
        </div>
        <div className="inline-flex items-center gap-1.5 text-xs text-[#5C665A] italic shrink-0">
          <Lock size={12} /> Consultation en ligne — Reproduction interdite
        </div>
      </header>
      <main
        className="flex-1 relative select-none"
        onContextMenu={(e) => e.preventDefault()}
      >
        <iframe
          title={doc.title}
          src={viewerSrc}
          className="absolute inset-0 w-full h-full border-0"
          sandbox="allow-scripts allow-same-origin"
          data-testid="document-viewer-frame"
        />
      </main>
      <footer className="bg-white border-t border-[#E5E3DB] px-4 py-2 text-center text-[11px] text-[#5C665A]">
        © CRESA ASBL — Tous droits réservés. Ce document est mis à disposition en consultation uniquement.
      </footer>
    </div>
  );
}
